import React, { useEffect, useMemo, useRef, useState } from "react";
import { MdChevronLeft, MdChevronRight, MdToday, MdCalendarMonth } from "react-icons/md";
import Card from "../../../components/card";
import MultiSelect from "../../../components/searchableSelect/MultiSelect";
import api from "../../../services/axios";
import { getEmailFromJwt } from "../../../utils/jwtUtils";
import { usePermissions } from "../../../auth/PermissionProvider";
import { Permissions } from "../../../auth/permissions";
import { getDaily, getDashboard, getTimeline } from "../../../services/capacityApi";
import {
  CapacityDailyResponse,
  CapacityDashboardResponse,
  CapacityTimelineResponse,
} from "../../../interfaces/Capacity";
import CapacityDashboard from "./components/CapacityDashboard";
import CapacityTimeline from "./components/CapacityTimeline";
import CapacityDaily from "./components/CapacityDaily";
import CapacityMonthly from "./components/CapacityMonthly";
import CapacityTeam from "./components/CapacityTeam";
import CapacityCalendar from "./components/CapacityCalendar";
import CapacityEmployeeTasks from "./components/CapacityEmployeeTasks";
import CapacityEmployeePlanner from "./components/CapacityEmployeePlanner";
import EmployeeCapacityManager from "./components/EmployeeCapacityManager";
import BudgetVsActualPanel from "./components/BudgetVsActualPanel";

interface Option {
  id: number;
  name: string;
  /** Usado só para descobrir qual colaborador é o usuário logado (não existe claim de employeeId). */
  email?: string;
}

type Tab = "load" | "dashboard" | "budget" | "calendar" | "team";
type Granularity = "day" | "week" | "month";
const GRAN_LABEL: Record<Granularity, string> = { day: "Dia", week: "Semana", month: "Mês" };

// Quantas unidades cada granularidade exibe na visão de carga.
const DAILY_DAYS = 14;
const WEEKLY_WEEKS = 9;
const MONTHLY_WEEKS = 26;

const DAY_MS = 24 * 60 * 60 * 1000;
const isoMonday = (d: Date) => {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = (date.getDay() + 6) % 7;
  return new Date(date.getTime() - diff * DAY_MS);
};
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY_MS);
const pad = (n: number) => String(n).padStart(2, "0");
const toDateInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmt = (d: Date) => d.toLocaleDateString("pt-BR");
const fmtShort = (d: Date) =>
  d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

const CapacityPage: React.FC = () => {
  const { can } = usePermissions();
  const [tab, setTab] = useState<Tab>("load");
  const [granularity, setGranularity] = useState<Granularity>("week");
  const [weekStart, setWeekStart] = useState<Date>(isoMonday(new Date()));
  const [dashboard, setDashboard] = useState<CapacityDashboardResponse | null>(null);
  const [timeline, setTimeline] = useState<CapacityTimelineResponse | null>(null);
  const [daily, setDaily] = useState<CapacityDailyResponse | null>(null);
  const [employees, setEmployees] = useState<Option[]>([]);
  const [projects, setProjects] = useState<Option[]>([]);
  const [employeeFilter, setEmployeeFilter] = useState<(string | number)[]>([]);
  const [loading, setLoading] = useState(false);

  // Quem só tem `capacity.view` enxerga Carga e Dashboard; abas/ações de gestão são ocultadas.
  const canManage = can(Permissions.CapacityManage);

  // Sem claim de employeeId no JWT: cruza o e-mail do token com a lista de colaboradores que a
  // página já carrega. Serve só para o colaborador planejar a própria semana — o servidor é a
  // autoridade (PUT /ProjectTask/{id}/schedule recusa tarefa de terceiro com 403).
  const myEmployeeId = useMemo(() => {
    const email = getEmailFromJwt(localStorage.getItem("jwtToken"))?.toLowerCase();
    if (!email) return null;
    return employees.find((e) => e.email?.toLowerCase() === email)?.id ?? null;
  }, [employees]);

  // O planner só aparece para quem está no filtro. Para o colaborador isso esconderia a tela
  // atrás de um passo não óbvio, então ele já entra filtrado em si mesmo — uma vez só, para não
  // brigar com quem depois limpar ou trocar o filtro.
  const seededOwnFilter = useRef(false);
  useEffect(() => {
    if (canManage || seededOwnFilter.current || myEmployeeId == null) return;
    seededOwnFilter.current = true;
    setEmployeeFilter([myEmployeeId]);
  }, [canManage, myEmployeeId]);

  useEffect(() => {
    api
      .get("/Employee/employees")
      .then((r) =>
        setEmployees(
          (Array.isArray(r.data) ? r.data : [])
            .filter((e: any) => e.flActive)
            .map((e: any) => ({ id: e.id, name: e.nmEmployee ?? e.email, email: e.email }))
        )
      )
      .catch((e) => console.error("Erro ao carregar colaboradores", e));

    api
      .get("/Project/Projects")
      .then((r) => {
        const list = (Array.isArray(r.data) ? r.data : []).filter((p: any) => p.status == null || p.status === 1);
        setProjects(
          list.map((p: any) => ({
            id: p.id,
            name: p.clientName ? `${p.projectName} — ${p.clientName}` : p.projectName,
          }))
        );
      })
      .catch((e) => console.error("Erro ao carregar projetos", e));
  }, []);

  const reload = () => {
    const tasks: Promise<void>[] = [];

    if (tab === "dashboard") {
      tasks.push(getDashboard(weekStart).then((d) => setDashboard(d)));
    } else if (tab === "load") {
      if (granularity === "day") {
        tasks.push(getDaily(weekStart, DAILY_DAYS).then((d) => setDaily(d)));
      } else {
        const weeks = granularity === "month" ? MONTHLY_WEEKS : WEEKLY_WEEKS;
        tasks.push(getTimeline(weekStart, weeks).then((t) => setTimeline(t)));
      }
    }

    if (tasks.length === 0) return;
    setLoading(true);
    Promise.all(tasks)
      .catch((e) => console.error("Erro ao carregar capacity", e))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [weekStart, tab, granularity]);

  // ── Filtro de colaborador (multi-seleção); vazio = todos ──────────────────────
  const selectedEmpIds = useMemo(() => employeeFilter.map(Number), [employeeFilter]);
  // Quem o usuário pode PLANEJAR (kanban de semanas): gestor planeja qualquer colaborador
  // filtrado; colaborador planeja só a si mesmo. Ver/filtrar os colegas continua livre.
  const plannableEmpIds = useMemo(
    () => (canManage ? selectedEmpIds : selectedEmpIds.filter((id) => id === myEmployeeId)),
    [canManage, selectedEmpIds, myEmployeeId]
  );
  const applyEmpFilter = selectedEmpIds.length > 0;
  const namesById = useMemo(() => new Map(employees.map((e) => [e.id, e.name])), [employees]);
  // Semanas do drill-down conforme a aba/granularidade visível.
  const detailWeeks =
    tab === "dashboard"
      ? 1
      : granularity === "month"
      ? MONTHLY_WEEKS
      : granularity === "day"
      ? Math.ceil(DAILY_DAYS / 7)
      : WEEKLY_WEEKS;

  const fTimeline = useMemo(() => {
    if (!timeline || !applyEmpFilter) return timeline;
    return { ...timeline, rows: timeline.rows.filter((r) => selectedEmpIds.includes(r.employeeId)) };
  }, [timeline, selectedEmpIds, applyEmpFilter]);

  const fDaily = useMemo(() => {
    if (!daily || !applyEmpFilter) return daily;
    return { ...daily, rows: daily.rows.filter((r) => selectedEmpIds.includes(r.employeeId)) };
  }, [daily, selectedEmpIds, applyEmpFilter]);

  const fDashboard = useMemo(() => {
    if (!dashboard || !applyEmpFilter) return dashboard;
    const emps = dashboard.employees.filter((e) => selectedEmpIds.includes(e.employeeId));
    const round = (n: number) => Math.round(n * 100) / 100;
    const sum = (pick: (e: (typeof emps)[number]) => number) => round(emps.reduce((a, e) => a + pick(e), 0));
    const teamCapacityHours = sum((e) => e.capacityHours);
    const teamAllocatedHours = sum((e) => e.allocatedHours);
    const teamEstimatedHours = sum((e) => e.estimatedHours);
    const teamWorkedHours = sum((e) => e.workedHours);
    const teamAvailableHours = sum((e) => e.availableHours);
    return {
      ...dashboard,
      employees: emps,
      teamCapacityHours,
      teamAllocatedHours,
      teamEstimatedHours,
      teamWorkedHours,
      teamAvailableHours,
      utilizationPct: teamCapacityHours > 0 ? Math.round((teamAllocatedHours / teamCapacityHours) * 100) : 0,
      overAllocatedCount: emps.filter((e) => e.utilizationPct >= 100).length,
    };
  }, [dashboard, selectedEmpIds, applyEmpFilter]);

  // Passo das setas: na visão mensal anda de mês em mês (4 semanas); senão, de semana em semana.
  const stepDays = tab === "load" && granularity === "month" ? 28 : 7;
  const shiftPeriod = (n: number) =>
    setWeekStart((prev) => new Date(prev.getTime() + n * stepDays * DAY_MS));

  const pickWeek = (value: string) => {
    if (!value) return;
    const [y, m, d] = value.split("-").map(Number);
    setWeekStart(isoMonday(new Date(y, m - 1, d)));
  };

  // Intervalo visível (depende da aba/granularidade) para o usuário saber o que está filtrado.
  const loadSpanDays =
    granularity === "day" ? DAILY_DAYS : (granularity === "month" ? MONTHLY_WEEKS : WEEKLY_WEEKS) * 7;
  const periodLabel =
    tab === "load"
      ? `${fmt(weekStart)} a ${fmt(addDays(weekStart, loadSpanDays - 1))}`
      : `Semana de ${fmtShort(weekStart)} a ${fmt(addDays(weekStart, 6))}`;

  return (
    <div className="mt-5 flex flex-col gap-4 animate-fade-up">
      <Card extra="p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="dv-aura flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow"
            >
              <MdCalendarMonth className="h-6 w-6" />
            </span>
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
                Controle de Capacity
              </span>
              <h3 className="mt-1 font-display text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
                {periodLabel}
              </h3>
            </div>
          </div>
          {(
            <div className="flex flex-wrap items-center gap-2">
              {(tab === "load" || tab === "dashboard") && (
                <div className="w-60" title="Filtrar por colaborador (vazio = todos)">
                  <MultiSelect
                    placeholder="Todos os colaboradores"
                    value={employeeFilter}
                    onChange={setEmployeeFilter}
                    options={employees.map((e) => ({ label: e.name, value: e.id }))}
                  />
                </div>
              )}
              {tab === "load" && (
                <div className="mr-1 flex rounded-xl border border-gray-200 p-0.5 dark:border-white/10">
                  {(["day", "week", "month"] as Granularity[]).map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGranularity(g)}
                      className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                        granularity === g
                          ? "bg-dv-gradient text-white shadow-dv-sm"
                          : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-navy-700"
                      }`}
                    >
                      {GRAN_LABEL[g]}
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                aria-label="Período anterior"
                onClick={() => shiftPeriod(-1)}
                className="rounded-xl border border-gray-200 p-2 text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
                title="Período anterior"
              >
                <MdChevronLeft size={18} aria-hidden="true" />
              </button>
              <input
                type="date"
                value={toDateInput(weekStart)}
                onChange={(e) => pickWeek(e.target.value)}
                title="Ir para a semana de uma data"
                aria-label="Ir para a semana de uma data"
                className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => setWeekStart(isoMonday(new Date()))}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
              >
                <MdToday size={16} aria-hidden="true" /> Hoje
              </button>
              <button
                type="button"
                aria-label="Próximo período"
                onClick={() => shiftPeriod(1)}
                className="rounded-xl border border-gray-200 p-2 text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
                title="Próximo período"
              >
                <MdChevronRight size={18} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <TabButton active={tab === "load"} onClick={() => setTab("load")}>Carga</TabButton>
          <TabButton active={tab === "dashboard"} onClick={() => setTab("dashboard")}>Dashboard</TabButton>
          <TabButton active={tab === "budget"} onClick={() => setTab("budget")}>Orçado × Realizado</TabButton>
          {canManage && (
            <TabButton active={tab === "calendar"} onClick={() => setTab("calendar")}>Calendário</TabButton>
          )}
          {canManage && (
            <TabButton active={tab === "team"} onClick={() => setTab("team")}>Avançado</TabButton>
          )}
        </div>
      </Card>

      <Card extra="p-4">
        {tab === "budget" ? (
          // Carrega os próprios dados ao montar — só quem abre a aba paga o custo.
          <BudgetVsActualPanel namesById={namesById} />
        ) : loading && tab !== "team" ? (
          <div className="py-10 text-center text-sm text-gray-700 dark:text-gray-300">Carregando...</div>
        ) : tab === "dashboard" ? (
          <CapacityDashboard data={fDashboard} />
        ) : tab === "load" ? (
          granularity === "day" ? (
            <CapacityDaily data={fDaily} />
          ) : granularity === "month" ? (
            <CapacityMonthly data={fTimeline} />
          ) : (
            <CapacityTimeline data={fTimeline} />
          )
        ) : tab === "calendar" ? (
          <CapacityCalendar employees={employees} onChanged={reload} />
        ) : (
          <div className="flex flex-col gap-6">
            <EmployeeCapacityManager employees={employees} />
            <CapacityTeam employees={employees} projects={projects} onChanged={reload} />
          </div>
        )}
      </Card>

      {/* Dashboard: planejador interativo. Gestor planeja qualquer colaborador filtrado;
          colaborador planeja apenas a própria semana (e sem quebrar tarefa para outra pessoa). */}
      {plannableEmpIds.length > 0 && tab === "dashboard" && (
        <div className="flex flex-col gap-4">
          {plannableEmpIds.map((id) => (
            <Card key={id} extra="p-4">
              <CapacityEmployeePlanner
                employeeId={id}
                employeeName={namesById.get(id) ?? `#${id}`}
                employees={employees}
                start={weekStart}
                weeks={8}
                canSplit={canManage}
              />
            </Card>
          ))}
        </div>
      )}

      {/* Carga: drill-down somente-leitura das tarefas que compõem o capacity. */}
      {selectedEmpIds.length > 0 && tab === "load" && (
        <Card extra="p-4">
          <CapacityEmployeeTasks
            employeeIds={selectedEmpIds}
            namesById={namesById}
            start={weekStart}
            weeks={detailWeeks}
          />
        </Card>
      )}
    </div>
  );
};

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all active:scale-[.98] ${
      active
        ? "bg-dv-gradient text-white shadow-dv-glow"
        : "bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-navy-900 dark:text-gray-300 dark:hover:bg-navy-700"
    }`}
  >
    {children}
  </button>
);

export default CapacityPage;
